import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { ScrambleText } from '@/components/motion/scramble-text'

type Verdict = 'Approved' | 'Declined' | 'StingBack'

type Sample = {
    id: string
    item: string
    amount: string
    verdict: Verdict
    ms: number
}

const ITEMS = ['Gift card', 'Crypto onramp', 'Game key', 'Prepaid top-up', 'In-game currency', 'Wallet funding']

// Deterministic so the server and the browser start on the same row.
const seeded = (seed: number): number => {
    const value = Math.sin(seed * 9301 + 49297) * 233280

    return value - Math.floor(value)
}

const SAMPLES: Sample[] = Array.from({ length: 24 }, (_, index) => ({
    id: Math.floor(seeded(index + 3) * 0xffffff).toString(16).toUpperCase().padStart(6, '0'),
    item: ITEMS[Math.floor(seeded(index + 41) * ITEMS.length)],
    amount: `$${(12 + seeded(index + 77) * 480).toFixed(2)}`,
    verdict: index % 9 === 4 ? 'Declined' : index % 11 === 7 ? 'StingBack' : 'Approved',
    ms: 96 + Math.floor(seeded(index + 13) * 360)
}))

const verdictColor: Record<Verdict, string> = {
    Approved: 'text-teal',
    Declined: 'text-ember',
    StingBack: 'text-lavender'
}

const barColor: Record<Verdict, string> = {
    Approved: 'bg-teal',
    Declined: 'bg-ember',
    StingBack: 'bg-lavender'
}

interface DecisionCardProps {
    className?: string
}

// An illustrative read-out of decisions arriving one after another.
export const DecisionCard: FC<DecisionCardProps> = ({ className }): ReactNode => {
    const reduce = useReducedMotion()
    const [index, setIndex] = useState(0)
    const sample = SAMPLES[index]
    const history = Array.from({ length: 18 }, (_, offset) => SAMPLES[(index + SAMPLES.length - offset) % SAMPLES.length]).reverse()

    useEffect(() => {
        if (reduce) return

        const timer = window.setInterval(() => setIndex(value => (value + 1) % SAMPLES.length), 1700)

        return () => {
            window.clearInterval(timer)
        }
    }, [reduce])

    return (
        <div aria-hidden className={cn('w-full max-w-[22rem] border border-white/10 bg-white/[0.04] p-5 text-mist backdrop-blur-xl', className)}>
            <div className='flex items-center justify-between t-label text-mist/50'>
                <span className='flex items-center gap-2'>
                    <span className='relative flex size-1.5'>
                        <span className='absolute inset-0 rounded-full bg-teal [animation:pulse-ring_2s_ease-out_infinite]' />
                        <span className='relative size-1.5 rounded-full bg-teal' />
                    </span>
                    Live decision
                </span>
                <span>
                    Illustrative
                </span>
            </div>

            <div className='mt-6 flex items-end justify-between gap-4'>
                <ScrambleText
                    text={sample.verdict.toUpperCase()}
                    duration={520}
                    className={cn('font-heading text-4xl font-semibold tracking-tight wdth-87', verdictColor[sample.verdict])}
                />
                <span className='pb-1 font-mono text-sm tabular-nums text-mist/70'>
                    {sample.ms}ms
                </span>
            </div>

            <div className='mt-4 grid grid-cols-3 gap-3 border-t border-white/10 pt-4 font-mono text-[0.6875rem] uppercase tracking-[0.08em]'>
                <span className='flex flex-col gap-1'>
                    <span className='text-mist/40'>
                        Tx
                    </span>
                    <span>
                        {sample.id}
                    </span>
                </span>
                <span className='flex flex-col gap-1'>
                    <span className='text-mist/40'>
                        Item
                    </span>
                    <span className='truncate'>
                        {sample.item}
                    </span>
                </span>
                <span className='flex flex-col gap-1 text-right'>
                    <span className='text-mist/40'>
                        Amount
                    </span>
                    <span className='tabular-nums'>
                        {sample.amount}
                    </span>
                </span>
            </div>

            <div className='mt-5 flex h-10 items-end gap-[3px]'>
                {history.map((entry, position) => (
                    <motion.span
                        key={`${entry.id}-${position}`}
                        layout
                        className={cn('flex-1', barColor[entry.verdict], position === history.length - 1 ? 'opacity-100' : 'opacity-50')}
                        style={{ height: `${30 + (entry.ms / 456) * 70}%` }}
                    />
                ))}
            </div>

            <div className='mt-4 flex justify-between t-label text-mist/50'>
                <span>
                    Approval up to 98%
                </span>
                <span>
                    &lt; 500ms
                </span>
            </div>
        </div>
    )
}
